import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Sparkles, ArrowLeft, LayoutDashboard, LogOut } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { authService, subscriptionService } from "../services/api";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const plans = [
  {
    id: "basico",
    name: "Básico",
    price: 299,
    properties: 1,
    description: "Ideal para agentes independientes",
    features: [
      "1 Plantilla (Clásica)",
      "Colores personalizados",
      "Formulario de contacto y/o WhatsApp",
      "Galería de hasta 10 fotos",
      "Video de YouTube / Vimeo",
      "Tracking pixels (Meta, GA, Ads)",
      "Dashboard de leads + notificaciones",
      "Exportación CSV de leads",
      "Soporte por email"
    ],
    popular: false
  },
  {
    id: "pro",
    name: "Pro",
    price: 599,
    properties: 5,
    description: "Para agentes con múltiples propiedades",
    features: [
      "Todo lo del plan Básico +",
      "3 Plantillas de landing (Clásica, Moderna, Elegante)",
      "Redactor con IA (descripciones, títulos y mensajes)",
      "Integración CRM (webhook de leads)",
      "Estadísticas de visitas",
      "Soporte prioritario"
    ],
    popular: true
  },
  {
    id: "premium",
    name: "Premium",
    price: 1099,
    properties: 20,
    description: "Para equipos y brokers",
    features: [
      "Todo lo del plan Pro +",
      "Dominio personalizado",
      "Múltiples usuarios",
      "Contacto directo con soporte"
    ],
    popular: false
  },
  {
    id: "agencia",
    name: "Agencia",
    price: 3999,
    properties: 50,
    description: "Para inmobiliarias y desarrolladores",
    features: [
      "Todo lo del plan Premium +",
      "White-label (tu marca)",
      "Soporte 24/7",
      "Onboarding personalizado"
    ],
    popular: false
  }
];

export const Pricing = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);

  const [isPendingPayment, setIsPendingPayment] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      setIsLoggedIn(false);
      return;
    }
    
    try {
      // Verify token is valid by calling API
      const subStatus = await subscriptionService.getStatus();
      setIsLoggedIn(true);
      setCurrentPlan(subStatus?.plan);
      setIsPendingPayment(subStatus?.is_pending_payment || subStatus?.status === "pending_payment");
      // Load user info for header display
      try {
        const user = await authService.getCurrentUser();
        setCurrentUser(user);
      } catch (e) { /* non-critical */ }
    } catch (error) {
      // Token invalid or expired - clear it
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setIsLoggedIn(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    authService.logout();
    navigate("/login");
  };

  const handlePlanSelect = (planId) => {
    const planPrices = { basico: 299, pro: 599, premium: 1099, agencia: 3999 };
    
    if (!isLoggedIn) {
      // Fire Lead + navigate with delay so event sends
      if (window.firePixelAndNavigate) {
        window.firePixelAndNavigate('Lead', { content_name: planId, value: planPrices[planId] || 0, currency: 'MXN' }, `/register?plan=${planId}`);
      } else {
        window.location.href = `/register?plan=${planId}`;
      }
      return;
    }

    // User is logged in - fire Lead event (SPA, no navigation yet)
    if (window.fbq) {
      window.fbq('trackSingle', '2770682049943810', 'Lead', {
        content_name: planId, value: planPrices[planId] || 0, currency: 'MXN'
      });
    }

    if (planId === currentPlan && !isPendingPayment) {
      toast.info("Ya tienes este plan activo");
      return;
    }

    initiateStripeCheckout(planId);
  };

  const initiateStripeCheckout = async (planId) => {
    setUpgrading(planId);
    try {
      const response = await axios.post(`${API}/payments/create-checkout?plan_id=${planId}`);
      
      if (response.data.url) {
        // Fire InitiateCheckout - user is being redirected to Stripe
        if (window.fbq) {
          const planPrices = { basico: 299, pro: 599, premium: 1099, agencia: 3999 };
          window.fbq('trackSingle', '2770682049943810', 'InitiateCheckout', {
            content_name: planId,
            value: planPrices[planId] || 0,
            currency: 'MXN'
          });
        }
        window.location.href = response.data.url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(error.response?.data?.detail || "Error al iniciar el pago");
      setUpgrading(null);
    }
  };

  const getPlanButtonText = (plan) => {
    if (upgrading === plan.id) return "Redirigiendo a pago...";
    if (!isLoggedIn) return "Seleccionar Plan";
    if (isPendingPayment) return "Seleccionar y Pagar";
    if (plan.id === currentPlan) return "Plan Actual";
    
    // Determine if upgrade or downgrade
    const planOrder = ["basico", "pro", "premium", "agencia"];
    const currentIndex = planOrder.indexOf(currentPlan);
    const newIndex = planOrder.indexOf(plan.id);
    
    if (newIndex > currentIndex) return "Mejorar Plan";
    return "Cambiar Plan";
  };

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Cargando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <Link to="/">
              <img 
                src="https://customer-assets.emergentagent.com/job_proplandpro/artifacts/d99lgtpt_inmopages-fondoblanco.png" 
                alt="InmoPages" 
                className="h-8"
              />
            </Link>
            <div className="flex items-center space-x-2 sm:space-x-3">
              {isLoggedIn ? (
                <>
                  <Link to="/admin/dashboard" className="hidden sm:block">
                    <Button variant="ghost" size="sm" className="text-gray-700 hover:text-brand-purple" data-testid="pricing-go-to-dashboard">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Mi Panel
                    </Button>
                  </Link>
                  <Link to="/admin/dashboard" className="flex items-center gap-2 px-3 py-1.5 rounded-full hover:bg-gray-100 transition-colors" data-testid="pricing-user-menu">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-purple to-brand-pink flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                      {(currentUser?.name || "U").charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-gray-700 hidden sm:inline max-w-[120px] truncate">
                      {currentUser?.name?.split(" ")[0] || "Cuenta"}
                    </span>
                  </Link>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="text-gray-500 hover:text-red-600"
                    data-testid="pricing-logout-btn"
                  >
                    <LogOut className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Salir</span>
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost">Iniciar Sesión</Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-brand-purple hover:bg-brand-pink text-white">
                      Registrarse
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-4 text-center">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          {isLoggedIn ? (isPendingPayment ? "Elige tu Plan" : "Cambia tu Plan") : "Planes y Precios"}
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
          {isLoggedIn 
            ? (isPendingPayment 
                ? "Selecciona un plan para activar tu cuenta y comenzar a crear landing pages."
                : `Tu plan actual: ${currentPlan?.charAt(0).toUpperCase() + currentPlan?.slice(1) || 'Sin plan'}. Selecciona un plan para actualizar.`)
            : "Elige el plan que mejor se adapte a tus necesidades. Planes desde $299 MXN/mes."
          }
        </p>
        {!isLoggedIn && (
          <div className="inline-flex items-center bg-brand-purple/10 text-brand-purple px-4 py-2 rounded-full text-sm font-medium">
            <Sparkles className="w-4 h-4 mr-2" />
            Pago seguro con Stripe · Cancela cuando quieras
          </div>
        )}
      </section>

      {/* Plans Grid */}
      <section className="max-w-7xl mx-auto px-4 pb-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => {
            const isCurrentPlan = plan.id === currentPlan && !isPendingPayment;
            return (
              <Card 
                key={plan.id} 
                className={`relative transition-all duration-300 ${
                  plan.popular ? 'border-2 border-brand-purple shadow-xl scale-105' : 'border border-gray-200'
                } ${isCurrentPlan ? 'ring-2 ring-green-500' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-brand-purple text-white text-xs font-bold px-3 py-1 rounded-full">
                      MÁS POPULAR
                    </span>
                  </div>
                )}
                {isCurrentPlan && (
                  <div className="absolute -top-3 right-4">
                    <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      ACTUAL
                    </span>
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-gray-900">${plan.price.toLocaleString()}</span>
                    <span className="text-gray-600"> MXN/mes</span>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-3 mb-6">
                    <span className="text-2xl font-bold text-brand-purple">
                      {typeof plan.properties === 'number' ? plan.properties : plan.properties}
                    </span>
                    <span className="text-gray-600 ml-1">
                      {typeof plan.properties === 'number' ? (plan.properties === 1 ? 'propiedad' : 'propiedades') : ''}
                    </span>
                  </div>

                  <ul className="space-y-3 text-left mb-6">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button 
                    className={`w-full ${
                      isCurrentPlan ? 'bg-green-500 hover:bg-green-600 text-white cursor-default' :
                      plan.popular ? 'bg-brand-purple hover:bg-brand-pink text-white' : ''
                    }`}
                    variant={plan.popular || isCurrentPlan ? 'default' : 'outline'}
                    onClick={() => handlePlanSelect(plan.id)}
                    disabled={isCurrentPlan || upgrading}
                  >
                    {getPlanButtonText(plan)}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* FAQ - Only for non-logged users */}
      {!isLoggedIn && (
        <section className="bg-white py-16 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Preguntas Frecuentes</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-lg mb-2">¿Puedo cambiar de plan después?</h3>
                <p className="text-gray-600">Sí, puedes actualizar o bajar tu plan en cualquier momento. Los cambios se aplican inmediatamente.</p>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2">¿Qué pasa si cancelo mi suscripción?</h3>
                <p className="text-gray-600">Tus propiedades se pausan (no se eliminan) hasta que reactives tu plan. Puedes reactivarlas cuando quieras.</p>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2">¿Aceptan pagos en México?</h3>
                <p className="text-gray-600">Sí, aceptamos tarjetas de crédito/débito mexicanas e internacionales a través de Stripe.</p>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2">¿Puedo cancelar en cualquier momento?</h3>
                <p className="text-gray-600">Sí, no hay contratos ni permanencia mínima. Cancela cuando quieras sin penalizaciones.</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA - Only for non-logged users */}
      {!isLoggedIn && (
        <section className="bg-gradient-to-r from-brand-purple to-brand-pink py-16 px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            ¿Listo para vender más propiedades?
          </h2>
          <p className="text-white/90 mb-8 text-lg">
            Crea tu cuenta y elige tu plan para comenzar a generar leads.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-brand-purple hover:bg-gray-100">
              Comenzar Ahora
            </Button>
          </Link>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 px-4 text-center">
        <p>© 2025 InmoPages. Todos los derechos reservados.</p>
      </footer>
    </div>
  );
};

export default Pricing;
